import { useState } from "react";
import { ExternalLink, BarChart3, AlertCircle } from "lucide-react";
import { useContent } from "../context/ContentContext";

export default function DashboardEmbed() {
    const [iframeError, setIframeError] = useState(false);
    const { content } = useContent();
    const d = content.dashboard;

    return (
        <section id="stats" data-testid="dashboard-section" className="relative py-24 md:py-40 bg-black">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="font-mono text-meraki-gold text-sm">06</span>
                            <div className="w-8 h-px bg-meraki-gold" />
                            <span className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold">{d.eyebrow}</span>
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] text-meraki-text">
                            {d.heading_line1}<br />
                            <span className="text-meraki-gold">{d.heading_line2}</span>
                        </h2>
                        <p className="mt-6 text-meraki-muted max-w-lg leading-relaxed whitespace-pre-line">{d.paragraph}</p>
                    </div>
                    <a
                        href={d.looker_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="dashboard-open-btn"
                        className="inline-flex items-center gap-3 px-6 py-4 border border-meraki-gold text-meraki-gold text-xs uppercase tracking-[0.25em] font-semibold hover:bg-meraki-gold hover:text-black transition-all"
                    >
                        <ExternalLink className="w-4 h-4" />
                        {d.cta_label}
                    </a>
                </div>

                <div className="relative border border-meraki-gold/30 bg-meraki-surface" data-testid="looker-embed-wrap">
                    <div className="absolute -top-3 left-6 bg-black px-3 text-[10px] uppercase tracking-[0.3em] text-meraki-gold">
                        <BarChart3 className="w-3 h-3 inline mr-2" />
                        Live Dashboard
                    </div>

                    {!iframeError ? (
                        <iframe
                            title="Meraki Riders Looker Studio Dashboard"
                            src={d.looker_embed_url}
                            className="w-full h-[600px] md:h-[720px] bg-black"
                            frameBorder="0"
                            allowFullScreen
                            sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                            onError={() => setIframeError(true)}
                            data-testid="looker-iframe"
                        />
                    ) : (
                        <div className="p-12 md:p-20 text-center" data-testid="looker-fallback">
                            <AlertCircle className="w-12 h-12 text-meraki-gold mx-auto mb-4" />
                            <h3 className="font-heading text-2xl uppercase tracking-wider text-meraki-text mb-3">Dashboard preview restricted</h3>
                            <p className="text-meraki-muted max-w-xl mx-auto mb-8">
                                The dashboard cannot be embedded here. Open the live view in a new tab to explore the numbers.
                            </p>
                            <a
                                href={d.looker_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-3 px-8 py-4 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                View Live Dashboard
                            </a>
                        </div>
                    )}
                </div>

                <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-meraki-muted text-center">
                    If the embed appears blank, click "{d.cta_label}" above.
                </div>
            </div>
        </section>
    );
}
